import { useMemo } from 'react';
import type { MapShape } from '@larki/mp/doc';
import {
  _2dMapCombinedQuote,
  _2dMapQuote,
} from '@larki/shared/type/order.v2';
import { redistributeOverriddenItems } from '@larki/shared/quote/redistribute-overridden-items';
import type { ActivityMetadata } from '@larki/shared/types/activity.type';
import type { JobT } from '@larki/shared/types/job.type';
import {
  useLazyGetMap2dQuery,
  useReplayMap2dMutation,
} from '../../../redux/rtk';
import { useShapeScrenshot } from '../../../mp/hooks/useShapeScreenshot';
import { quote2dMapWorker } from '../../../utilities/quote2dMap';
import { getUser } from '../../../utilities/storage';
import { softwares } from '../../../components/mapView/constants';
import { QuotePdfProps } from '../../../components/FileManager/PdfGenerator';
import { resolveMap2dBytes } from './resolveMap2dBytes';

// Runtime carries fields beyond JobT (updater, updated_at) — declared
// explicitly so consumer adds don't silently drift.
type ActivityJob = Pick<
  JobT,
  'id' | 'full_address' | 'map2d_id' | 'customer_software'
> & {
  updater?: { full_name?: string | null } | null;
  updated_at: string | Date;
};

type ActivityProject = {
  id?: number;
  name?: string | null;
  organisation?: { name?: string | null } | null;
  creator?: { email?: string | null } | null;
};

type Args = {
  metadata: ActivityMetadata | null | undefined;
  job: ActivityJob | undefined;
  currentProject: ActivityProject;
};

export function useQuotePdfProps({ metadata, job, currentProject }: Args) {
  const [getMap2d] = useLazyGetMap2dQuery();
  const [replayMap2d] = useReplayMap2dMutation();
  const shapeScrenshot = useShapeScrenshot();

  return useMemo(() => {
    if (
      !metadata ||
      (metadata.variant !== 'quote_created' &&
        metadata.variant !== 'request_for_quote') ||
      !job ||
      !currentProject ||
      !job.map2d_id
    )
      return null;
    const md = metadata;
    const map2dId = job.map2d_id;
    const type = md.variant === 'request_for_quote' ? 'estimate' : 'quote';

    function unwrapQuote(quote: _2dMapQuote) {
      return {
        charges: Object.values(quote.composition).flatMap((q) => q.charges),
        totals: quote.totals,
      };
    }

    function unwrapCombinedQuote(combinedQuote: _2dMapCombinedQuote) {
      return {
        charges: [
          ...Object.values(combinedQuote.nonRfq.composition).flatMap(
            (q) => q.charges
          ),
          ...Object.values(combinedQuote.rfq.composition).flatMap(
            (q) => q.charges
          ),
        ],
        totals: combinedQuote.totals,
      };
    }

    // Overrides are stored as product keys on the RFQ side, not pre-applied;
    // redistribute folds them into the BUY NOW slice for display.
    const redistributed =
      md.variant === 'quote_created' && 'nonRfq' in md.quote_data
        ? redistributeOverriddenItems(md.quote_data)
        : null;

    function unwrap() {
      if (md.variant === 'quote_created') {
        if (redistributed) return unwrapQuote(redistributed.buyNowQuote);
        return unwrapQuote(md.quote_data as unknown as _2dMapQuote);
      }
      return unwrapCombinedQuote(md.request_for_quote_data);
    }

    const { charges, totals } = unwrap();

    const sourceCustomOrders =
      md.variant === 'request_for_quote'
        ? md.request_for_quote_data.customOrders
        : (redistributed?.buyNowCustomOrders ?? undefined);

    // Replay the map state captured at activity-commit time so the PDF
    // reflects only shapes that were part of the priced quote — not any
    // shapes the user added afterwards. Legacy activities (no frontiers
    // stored) and replay failures fall back to the live map.
    const activityFrontiers = md.map2d_frontiers ?? null;

    return async () => {
      const map2d = await resolveMap2dBytes({
        map2dId,
        activityFrontiers,
        replay: (args) => replayMap2d(args).unwrap(),
        getLive: (args) => getMap2d(args).unwrap(),
      });
      const dataType2Screenshot = new Map<string, string | null>();
      const customOrderId2Preview = new Map<
        string,
        { image: string | null; scopeArea: number | null }
      >();
      if (map2d.bytes) {
        // Lazy-import: MpDoc pulls Loro WASM (~MB). Importing eagerly would
        // cost every JobTimeline render even when no PDF is generated.
        const [{ MpDoc }, { Buffer }] = await Promise.all([
          import('@larki/mp/doc'),
          import('buffer'),
        ]);
        const map2dBytes = new Uint8Array(Buffer.from(map2d.bytes, 'base64'));
        const map2dDoc = MpDoc.fromSnapshot({ state: map2dBytes });
        const shapes = map2dDoc.getShapes();

        const shapesByDataType = new Map<string, MapShape[]>();
        const shapesByCustomOrderId = new Map<string, MapShape[]>();
        for (const shape of shapes) {
          if (shape.dataType) {
            const arr = shapesByDataType.get(shape.dataType);
            if (arr) arr.push(shape);
            else shapesByDataType.set(shape.dataType, [shape]);
          }
          if (shape.customOrderId) {
            const arr = shapesByCustomOrderId.get(shape.customOrderId);
            if (arr) arr.push(shape);
            else shapesByCustomOrderId.set(shape.customOrderId, [shape]);
          }
        }

        const dataTypePromises: Promise<
          readonly [string, string | null]
        >[] = [];
        for (const charge of charges) {
          const dataType = charge.product;
          const shapesOfDataType = shapesByDataType.get(dataType);
          if (shapesOfDataType?.length) {
            dataTypePromises.push(
              (async () => {
                const screenshot =
                  await shapeScrenshot.generate(shapesOfDataType);
                return [dataType, screenshot] as const;
              })()
            );
          }
        }

        const customOrderGroups = (sourceCustomOrders ?? [])
          .map((order) => ({
            order,
            orderShapes: shapesByCustomOrderId.get(order.id) ?? [],
          }))
          .filter(({ orderShapes }) => orderShapes.length > 0);

        // Per-shape area is computed independently per shape, so we only need
        // to ship custom-order shapes to bound the worker payload.
        const customOrderShapes = customOrderGroups.flatMap(
          (g) => g.orderShapes
        );
        // Backend-authoritative per-shape area (geodesic via pyproj) — matches
        // the cart row's shape_area_in_sqm so PDF and cart agree.
        const coverageShapesPromise = customOrderShapes.length
          ? quote2dMapWorker({
              shapes: customOrderShapes,
              sessionToken: getUser()?.token ?? '',
            })
              .then(({ coverage }) => coverage.shapes ?? null)
              .catch(() => null)
          : Promise.resolve(null);

        const customOrderPromises = customOrderGroups.map(
          ({ order, orderShapes }) =>
            Promise.all([
              shapeScrenshot.generate(orderShapes),
              coverageShapesPromise,
            ]).then(([image, coverageShapes]) => {
              const scopeArea =
                orderShapes.reduce(
                  (sum, s) =>
                    sum + (coverageShapes?.[s.id]?.shape_area_in_sqm ?? 0),
                  0
                ) || null;
              return [order.id, { image, scopeArea }] as const;
            })
        );

        const [dataTypeResults, customOrderResults] = await Promise.all([
          Promise.all(dataTypePromises),
          Promise.all(customOrderPromises),
        ]);
        for (const [dataType, screenshot] of dataTypeResults) {
          dataType2Screenshot.set(dataType, screenshot);
        }
        for (const [orderId, preview] of customOrderResults) {
          customOrderId2Preview.set(orderId, preview);
        }
      }

      const siteDeploymentFee =
        type === 'estimate'
          ? totals.fees.find(
              (fee) => fee.metadata.id === 'site_deployment_fee'
            )
          : null;
      const totalNoTax = totals.totalGross;
      const tax = totals.taxesTotal;
      const total = totals.totalNet;
      const result: QuotePdfProps = {
        data: {
          type,
          metadata: {
            lastUpdateBy: job.updater?.full_name,
            company: currentProject.organisation?.name,
            date: new Date(job.updated_at),
            email: currentProject.creator?.email,
            jobId: job.id,
            projectId: currentProject?.id,
          },
          siteAddress: job.full_address,
          ...(type === 'quote'
            ? {
                projectName: currentProject.name ?? 'Untitled',
                software: softwares.entities[job.customer_software]?.name,
              }
            : {}),
          products: charges.map((charge) => ({
            image: dataType2Screenshot.get(charge.product),
            dataType: charge.product,
            deliveryMethod: 'manual',
            scopeArea: charge.group === 'membership' ? null : charge.quantity,
            price: charge.subtotal,
          })),
          siteDeploymentFee: siteDeploymentFee?.amount,
          customOrders: sourceCustomOrders?.length
            ? sourceCustomOrders.map((o) => {
                const preview = customOrderId2Preview.get(o.id);
                return {
                  id: o.id,
                  name: o.name,
                  description: o.description,
                  color: o.color,
                  price: o.price,
                  image: preview?.image ?? null,
                  scopeArea: preview?.scopeArea ?? null,
                };
              })
            : undefined,
          totalNoTax,
          tax,
          total,
        },
      };
      return result;
    };
  }, [
    currentProject,
    getMap2d,
    replayMap2d,
    job,
    metadata,
    shapeScrenshot,
  ]);
}
